/**
 * Export — of what you are looking at.
 *
 * The old control exported a date range from whichever of the two
 * pre-unification endpoints a radio picked, which had nothing to do with the
 * list on screen. It now takes the exact query string the list built and calls
 * the same endpoint with `paginate=false`, so "Export" means "these rows".
 *
 * The modal is a confirmation, not a form: it names the filters, states the row
 * count, and offers one escape hatch — ignore the filters and export a date
 * range — which is the old behaviour rebuilt on the unified endpoint.
 *
 * A third option sits beside them: the **home delivery run sheet**, which is a
 * different animal. It is not this list at all but a dispatch document — the
 * orders that *entered* READY inside a window, by thana, for the channels our
 * own riders deliver. It needs the address, the thana and the phone, none of
 * which are on the list endpoint, so unlike the other two it is built by the
 * server and arrives as a file (`/orders/runSheet`). It lives here rather than
 * behind a second button in the page header because one export entry point
 * beats two, and it is gated by the same EXPORT_ORDERS right.
 */

import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { DatePicker, TimePicker, Select, Modal, Button, ConfigProvider } from 'antd';
import { DownloadOutlined, PrinterOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';
import useAxiosClient from '../../axios-client';
import { useStateContext } from '../../contexts/ContextProvider';
import { toast } from 'react-toastify';
import { OrderStatusEnum } from '../../utils/enums/OrderStatusEnum';
import { OrderTypeEnum } from '../../utils/enums/OrderTypeEnum';
import {
  PRIMARY,
  hexToRgba,
  SHELL_THEME as THEME,
  shellAntdTheme,
  shellVars,
  ringOf,
} from '../../shellTokens';
import { RUN_SHEET_OPENS, RUN_SHEET_CLOSES, atHour, runSheetWindow } from './runSheetWindow';
import { runSheetCount } from './runSheetGroups';
import RunSheetDocument from './RunSheetDocument';
import CourierRunSheetDocument, { courierSheetCount } from './CourierRunSheetDocument';
import printInvoice from '../FabricInvoice/printInvoice';
import '../Util/formControls.css';

const MAX_RANGE_MONTHS = 2;

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

// Column key → header label and value reader. The export follows the list's
// visible columns, so hiding Team on screen hides it in the file too.
const FIELDS = {
  id: { header: 'Order ID', value: (o) => o.id },
  channel: { header: 'Channel', value: (o) => (o.orderType === OrderTypeEnum.MERCHANT ? 'Offline' : o.marketplace || 'Online') },
  orderedBy: { header: 'Ordered By', value: (o) => o.orderedBy },
  team: { header: 'Team', value: (o) => o.team || '' },
  createdBy: { header: 'Submitted By', value: (o) => o.createdBy },
  createdAt: { header: 'Order Date', value: (o) => (o.createdAt ? dayjs(o.createdAt).format('DD-MM-YYYY') : '') },
  deliveryDate: { header: 'Delivery Date', value: (o) => (o.deliveryDate ? dayjs(o.deliveryDate).format('DD-MM-YYYY') : '') },
  totalAmount: { header: 'Total Amount', value: (o) => Number(o.totalAmount) || 0 },
  status: { header: 'Status', value: (o) => (OrderStatusEnum.find((s) => s.value === o.status)?.label ?? 'Unknown') },
};
// Fallback for a caller that does not pass a column set (or hides everything).
const DEFAULT_KEYS = ['id', 'channel', 'orderedBy', 'createdBy', 'createdAt', 'deliveryDate', 'totalAmount', 'status'];

const ExportOrderExcel = ({ query = '', total = 0, filters = [], columns = [] }) => {
  const axiosClient = useAxiosClient();
  const { user, darkMode } = useStateContext();
  const themeVars = React.useMemo(() => ({
    ...shellVars(darkMode),
    '--focusRing': ringOf(PRIMARY),
  }), [darkMode]);
  const t = THEME[darkMode ? 'dark' : 'light'];
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  // 'view' = the rows on screen; 'range' = the date-range escape hatch;
  // 'runSheet' = the home delivery dispatch document;
  // 'courierSheet' = the courier delivery dispatch document.
  const [mode, setMode] = useState('view');
  const [dates, setDates] = useState(null);
  // The run sheet is a window inside one day, which is how dispatch reads it:
  // "what went ready today between 9 and 6".
  const [runDay, setRunDay] = useState(dayjs());
  const [runTimes, setRunTimes] = useState([atHour(RUN_SHEET_OPENS), atHour(RUN_SHEET_CLOSES)]);
  // What the window holds, loaded as soon as one is chosen: it fills the area
  // picker and it is what Print lays out, so the sheet on paper is the same
  // rows the modal just counted.
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  // Empty means every area — the normal run sheet.
  const [areaIds, setAreaIds] = useState([]);
  const [printing, setPrinting] = useState(false);
  // Courier run sheet — same window pickers, own preview and channel filter.
  const [courierDay, setCourierDay] = useState(dayjs());
  const [courierTimes, setCourierTimes] = useState([atHour(RUN_SHEET_OPENS), atHour(RUN_SHEET_CLOSES)]);
  const [courierPreview, setCourierPreview] = useState(null);
  const [courierPreviewing, setCourierPreviewing] = useState(false);
  const [channelIds, setChannelIds] = useState([]);
  const [courierPrinting, setCourierPrinting] = useState(false);

  const keys = columns.length
    ? columns.map((c) => c.key).filter((k) => FIELDS[k])
    : DEFAULT_KEYS;
  const exportKeys = keys.length ? keys : DEFAULT_KEYS;

  const close = () => { setOpen(false); setMode('view'); setDates(null); };

  const window_ = runSheetWindow(runDay, runTimes);
  // The rows the two run sheet buttons would act on, after the area choice.
  const runSheetRows = preview?.data ?? [];
  const selectedCount = runSheetCount(runSheetRows, areaIds);

  // Courier run sheet — own window, own preview, own count.
  const courierWindow = runSheetWindow(courierDay, courierTimes);
  const courierRows = courierPreview?.data ?? [];
  const courierSelectedCount = courierSheetCount(courierRows, channelIds);

  /**
   * Load what the chosen window holds. Runs whenever the window changes while
   * the run sheet option is open — the area picker cannot offer a list of every
   * upazila in Bangladesh and expect anyone to find Kafrul in it, so it offers
   * only the thanas this window actually contains, with a count each.
   */
  const loadPreview = useCallback(async (from, to) => {
    setPreviewing(true);
    try {
      const res = await axiosClient.get('/orders/runSheet/preview', { params: { from, to } });
      setPreview(res.data);
      // Areas from a previous window mean nothing in this one, and a stale id
      // would silently print an empty sheet.
      setAreaIds([]);
    } catch (error) {
      setPreview(null);
      toast.error(error.response?.data?.message || error.message);
    } finally {
      setPreviewing(false);
    }
  }, [axiosClient]);

  const loadCourierPreview = useCallback(async (from, to) => {
    setCourierPreviewing(true);
    try {
      const res = await axiosClient.get('/orders/courierRunSheet/preview', { params: { from, to } });
      setCourierPreview(res.data);
      setChannelIds([]);
    } catch (error) {
      setCourierPreview(null);
      toast.error(error.response?.data?.message || error.message);
    } finally {
      setCourierPreviewing(false);
    }
  }, [axiosClient]);

  useEffect(() => {
    if (!open || mode !== 'runSheet' || window_.error) return;
    loadPreview(window_.from, window_.to);
    // Keyed on the window itself, not the dayjs objects: the pickers hand back
    // new instances on every render, which would loop.
  }, [open, mode, window_.from, window_.to, window_.error, loadPreview]);

  useEffect(() => {
    if (!open || mode !== 'courierSheet' || courierWindow.error) return;
    loadCourierPreview(courierWindow.from, courierWindow.to);
  }, [open, mode, courierWindow.from, courierWindow.to, courierWindow.error, loadCourierPreview]);

  /** The run sheet on paper — one page per thana, so the stack splits by area. */
  const printRunSheet = async () => {
    if (window_.error) {
      toast.error(window_.error);
      return;
    }
    if (!selectedCount) {
      toast.error('Nothing in that window to print.');
      return;
    }

    setPrinting(true);
    try {
      const how = await printInvoice(
        <RunSheetDocument rows={runSheetRows} areaIds={areaIds} from={window_.from} to={window_.to} />,
      );
      if (how === 'opened') {
        toast.info('Your browser opened the run sheet in a new tab — print from there.');
      }
      close();
    } catch (error) {
      toast.error(error.message || 'Could not build the run sheet');
    } finally {
      setPrinting(false);
    }
  };

  /**
   * The run sheet, which the server builds. Everything else on this modal is
   * assembled in the browser from the list endpoint; this one arrives as a
   * finished file, so it needs the blob dance — including reading an error body
   * back as text, since asking for a blob means a failed request hands back a
   * blob of JSON rather than JSON.
   */
  const downloadRunSheet = async () => {
    const { from, to, filename, error } = window_;
    if (error) {
      toast.error(error);
      return;
    }

    setLoading(true);
    try {
      const res = await axiosClient.get('/orders/runSheet', {
        // axios serialises an array as `areas[]=320&areas[]=523`, which is what
        // Laravel's array validation reads, and drops the key entirely when the
        // array is empty — which is exactly "every area". Do not add a
        // paramsSerializer here: `indexes: null` sends `areas=320&areas=523`,
        // and PHP keeps only the last as a string, failing the `array` rule.
        params: { from, to, areas: areaIds },
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([res.data], { type: XLSX_MIME }));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      close();
    } catch (error) {
      // The error body came back as a blob because that is what was asked for,
      // so it has to be read as text before it can say anything useful.
      let message = 'Could not build the run sheet';
      try {
        const parsed = JSON.parse(await error.response?.data?.text());
        message = parsed.message || message;
      } catch { /* not JSON — keep the generic message */ }
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  /** The courier run sheet on paper — flat list, no thana grouping. */
  const printCourierSheet = async () => {
    if (courierWindow.error) {
      toast.error(courierWindow.error);
      return;
    }
    if (!courierSelectedCount) {
      toast.error('Nothing in that window to print.');
      return;
    }

    setCourierPrinting(true);
    try {
      const how = await printInvoice(
        <CourierRunSheetDocument rows={courierRows} channelIds={channelIds} from={courierWindow.from} to={courierWindow.to} />,
      );
      if (how === 'opened') {
        toast.info('Your browser opened the run sheet in a new tab — print from there.');
      }
      close();
    } catch (error) {
      toast.error(error.message || 'Could not build the courier run sheet');
    } finally {
      setCourierPrinting(false);
    }
  };

  const downloadCourierSheet = async () => {
    const { from, to, error } = courierWindow;
    if (error) {
      toast.error(error);
      return;
    }

    setLoading(true);
    try {
      const cFrom = courierDay.hour(courierTimes[0].hour()).minute(courierTimes[0].minute()).second(0);
      const cTo = courierDay.hour(courierTimes[1].hour()).minute(courierTimes[1].minute()).second(0);
      const span = cFrom.format('YYYYMMDD-HHmm') + '-to-' + cTo.format('HHmm');

      const res = await axiosClient.get('/orders/courierRunSheet', {
        params: { from, to, channels: channelIds },
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([res.data], { type: XLSX_MIME }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `courier-run-sheet-${span}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      close();
    } catch (error) {
      let message = 'Could not build the courier run sheet';
      try {
        const parsed = JSON.parse(await error.response?.data?.text());
        message = parsed.message || message;
      } catch { }
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const run = async () => {
    if (mode === 'runSheet') {
      return downloadRunSheet();
    }
    if (mode === 'courierSheet') {
      return downloadCourierSheet();
    }

    let requestQuery;
    let filenameSuffix;

    if (mode === 'range') {
      if (!dates || !dates[0] || !dates[1]) {
        toast.error('Please select a date range');
        return;
      }
      if (dates[1].diff(dates[0], 'month') > MAX_RANGE_MONTHS) {
        toast.error(`Please select a date range of up to ${MAX_RANGE_MONTHS} months.`);
        return;
      }
      // Order date, not delivery — same as the control this replaces.
      requestQuery = `orderDateStart=${dates[0].format('YYYY-MM-DD')}&orderDateEnd=${dates[1].format('YYYY-MM-DD')}`;
      filenameSuffix = `${dates[0].format('YYYYMMDD')}-${dates[1].format('YYYYMMDD')}`;
    } else {
      requestQuery = query;
      filenameSuffix = dayjs().format('YYYYMMDD');
    }

    setLoading(true);
    try {
      const url = `/orders/getOrders/${user.id}?${[requestQuery, 'paginate=false'].filter(Boolean).join('&')}`;
      const res = await axiosClient.get(url);
      const rows = res.data?.data || [];
      if (rows.length === 0) {
        toast.error('No orders match — nothing to export.');
        return;
      }
      const sheetRows = rows.map((o) => Object.fromEntries(
        exportKeys.map((k) => [FIELDS[k].header, FIELDS[k].value(o)]),
      ));
      const worksheet = XLSX.utils.json_to_sheet(sheetRows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Orders');
      XLSX.writeFile(workbook, `Orders_${filenameSuffix}.xlsx`);
      close();
    } catch (error) {
      toast.error(error.response?.data?.message || error.message);
    } finally {
      setLoading(false);
    }
  };

  /**
   * The run sheet's own footer.
   *
   * A ReactNode, not a `(originNode) => ReactNode` render function: antd is
   * pinned at 5.4.2 here, where `footer` is typed `React.ReactNode` and is
   * passed straight through to the dialog. The render-function form arrived in
   * 5.9 — handing 5.4.2 a function rendered nothing at all, which is how this
   * modal lost every button the moment the run sheet was selected.
   */
  const runSheetFooter = (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8, paddingTop: 8, borderTop: `1px solid ${t.border}` }}>
      <Button
        onClick={close}
        style={{
          height: 36,
          padding: '0 14px',
          borderRadius: 8,
          border: `1px solid ${t.border}`,
          background: t.surface2,
          color: t.text,
          font: '600 12.5px Outfit, sans-serif',
        }}
      >
        Cancel
      </Button>
      <Button
        icon={<PrinterOutlined />}
        loading={printing}
        disabled={!selectedCount}
        onClick={printRunSheet}
        style={{
          height: 36,
          padding: '0 14px',
          borderRadius: 8,
          border: `1px solid ${t.border}`,
          background: t.surface2,
          color: t.text,
          font: '600 12.5px Outfit, sans-serif',
        }}
      >
        Print
      </Button>
      <Button
        type="primary"
        icon={<DownloadOutlined />}
        loading={loading}
        disabled={!selectedCount}
        onClick={run}
        style={{
          height: 36,
          padding: '0 16px',
          borderRadius: 8,
          border: 'none',
          background: PRIMARY,
          color: '#fff',
          font: '600 12.5px Outfit, sans-serif',
          boxShadow: 'none',
        }}
      >
        Download Excel{selectedCount ? ` (${selectedCount})` : ''}
      </Button>
    </div>
  );

  const courierSheetFooter = (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8, paddingTop: 8, borderTop: `1px solid ${t.border}` }}>
      <Button
        onClick={close}
        style={{
          height: 36,
          padding: '0 14px',
          borderRadius: 8,
          border: `1px solid ${t.border}`,
          background: t.surface2,
          color: t.text,
          font: '600 12.5px Outfit, sans-serif',
        }}
      >
        Cancel
      </Button>
      <Button
        icon={<PrinterOutlined />}
        loading={courierPrinting}
        disabled={!courierSelectedCount}
        onClick={printCourierSheet}
        style={{
          height: 36,
          padding: '0 14px',
          borderRadius: 8,
          border: `1px solid ${t.border}`,
          background: t.surface2,
          color: t.text,
          font: '600 12.5px Outfit, sans-serif',
        }}
      >
        Print
      </Button>
      <Button
        type="primary"
        icon={<DownloadOutlined />}
        loading={loading}
        disabled={!courierSelectedCount}
        onClick={run}
        style={{
          height: 36,
          padding: '0 16px',
          borderRadius: 8,
          border: 'none',
          background: PRIMARY,
          color: '#fff',
          font: '600 12.5px Outfit, sans-serif',
          boxShadow: 'none',
        }}
      >
        Download Excel{courierSelectedCount ? ` (${courierSelectedCount})` : ''}
      </Button>
    </div>
  );

  const disabledDate = (current) => current && current > dayjs().endOf('day');

  const optionStyle = (on) => ({
    border: `1px solid ${on ? PRIMARY : t.border}`,
    background: on ? hexToRgba(PRIMARY, .08) : t.surface2,
    borderRadius: 10,
    padding: '12px 14px',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    cursor: 'pointer',
    color: t.text,
    transition: 'all .12s ease',
  });
  const radio = (on) => ({
    width: 16, height: 16, borderRadius: 8, flexShrink: 0,
    background: on ? PRIMARY : 'transparent',
    border: on ? `1px solid ${PRIMARY}` : `1.5px solid ${t.borderStrong}`,
    color: '#fff', fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center',
  });

  return (
    <Fragment>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="Export the orders currently listed"
        style={{
          height: 38, padding: '0 14px', borderRadius: 9, border: '1px solid var(--border)',
          background: 'var(--surface)', color: 'var(--text)', font: '600 13px Outfit, sans-serif',
          cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 7,
        }}
      >
        <DownloadOutlined style={{ color: 'var(--muted)', fontSize: 12 }} />
        Export
        <span style={{ font: '600 11px var(--mono, monospace)', color: 'var(--dim)', padding: '2px 6px', borderRadius: 5, background: 'var(--surface2)' }}>
          {total}
        </span>
      </button>

      <ConfigProvider theme={shellAntdTheme(darkMode)}>
        <Modal
          open={open}
          title={(
            <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
              <DownloadOutlined style={{ fontSize: 17, color: PRIMARY }} />
              <span style={{ font: '700 16px Outfit, sans-serif', color: t.text }}>
                Export orders
              </span>
            </div>
          )}
          width={480}
          onCancel={close}
          confirmLoading={loading}
          onOk={run}
          style={themeVars}
          wrapClassName="et-controls"
          className="et-controls et-export-modal"
          okText={{
            view: `Export ${total} order${total === 1 ? '' : 's'}`,
            range: 'Export date range',
          }[mode]}
          okButtonProps={{
            disabled: mode === 'view' && total === 0,
            style: {
              background: PRIMARY,
              borderRadius: 8,
              height: 36,
              font: '600 12.5px Outfit, sans-serif',
              border: 'none',
              boxShadow: 'none',
            },
          }}
          cancelButtonProps={{
            style: {
              background: t.surface2,
              border: `1px solid ${t.border}`,
              color: t.text,
              borderRadius: 8,
              height: 36,
              font: '600 12.5px Outfit, sans-serif',
            },
          }}
          footer={mode === 'runSheet' ? runSheetFooter : mode === 'courierSheet' ? courierSheetFooter : undefined}
        >
          <style>{`
            .et-export-modal {
              font-family: Outfit, system-ui, sans-serif;
              color: var(--text);
            }
            .et-export-modal .ant-picker,
            .et-export-modal .ant-select:not(.ant-select-customize-input) .ant-select-selector {
              background: var(--surface2) !important;
              border: 1px solid var(--border) !important;
              border-radius: 8px !important;
              color: var(--text) !important;
              font-family: Outfit, system-ui, sans-serif !important;
            }
            .et-export-modal .ant-picker-input > input {
              font-family: 'JetBrains Mono', monospace !important;
              font-size: 12.5px !important;
              color: var(--text) !important;
            }
            .et-export-modal .ant-picker-suffix,
            .et-export-modal .ant-select-arrow {
              color: var(--dim) !important;
            }
            .et-export-modal .ant-picker-focused,
            .et-export-modal .ant-select-focused:not(.ant-select-disabled) .ant-select-selector {
              border-color: var(--primary) !important;
              box-shadow: 0 0 0 3px var(--focusRing, rgba(217, 119, 6, 0.12)) !important;
            }
            .et-export-modal .ant-select-selection-item {
              color: var(--text) !important;
              font-family: Outfit, sans-serif !important;
              font-size: 12.5px !important;
            }
            .et-export-modal .ant-select-selection-placeholder {
              color: var(--dim) !important;
              font-family: Outfit, sans-serif !important;
              font-size: 12.5px !important;
            }
          `}</style>

          <div className="et-controls" style={{ ...themeVars, display: 'flex', flexDirection: 'column', gap: 14, paddingTop: 6 }}>

            <div onClick={() => setMode('view')} style={optionStyle(mode === 'view')}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                <span style={radio(mode === 'view')}>{mode === 'view' && '✓'}</span>
                <span style={{ font: '600 13px Outfit, sans-serif' }}>Current view</span>
                <span style={{ marginLeft: 'auto', font: '700 12px var(--mono, monospace)', color: t.activeText }}>
                  {total} order{total === 1 ? '' : 's'}
                </span>
              </div>
              {filters.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, paddingLeft: 24 }}>
                  {filters.map((f) => (
                    <span key={f} style={{ font: '600 11px Outfit, sans-serif', color: t.muted, background: t.surface2, border: `1px solid ${t.border}`, padding: '2px 7px', borderRadius: 6 }}>
                      {f}
                    </span>
                  ))}
                </div>
              )}
              <div style={{ paddingLeft: 24, font: '400 11.5px Outfit, sans-serif', color: t.dim }}>
                {filters.length === 0 && 'No filters applied. '}
                {exportKeys.length} column{exportKeys.length === 1 ? '' : 's'} · <span style={{ fontFamily: 'var(--mono, monospace)' }}>/orders/getOrders</span>
              </div>
            </div>

            <div onClick={() => setMode('range')} style={optionStyle(mode === 'range')}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                <span style={radio(mode === 'range')}>{mode === 'range' && '✓'}</span>
                <span style={{ font: '600 13px Outfit, sans-serif', color: mode === 'range' ? t.text : t.muted }}>
                  Ignore filters — export a date range
                </span>
              </div>
              <div style={{ paddingLeft: 24 }} onClick={(e) => e.stopPropagation()}>
                <DatePicker.RangePicker
                  format="DD/MM/YYYY"
                  value={dates}
                  onChange={(d) => { setDates(d); setMode('range'); }}
                  disabledDate={disabledDate}
                  style={{ width: '100%' }}
                />
              </div>
              <div style={{ paddingLeft: 24, font: '400 11.5px Outfit, sans-serif', color: t.dim }}>
                Max {MAX_RANGE_MONTHS} months. Same endpoint; order date, not delivery.
              </div>
            </div>

            {/* Not this list at all — a dispatch document. Deliberately last:
                it answers a different question from the two above it. */}
            <div onClick={() => setMode('runSheet')} style={optionStyle(mode === 'runSheet')}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                <span style={radio(mode === 'runSheet')}>{mode === 'runSheet' && '✓'}</span>
                <span style={{ font: '600 13px Outfit, sans-serif', color: mode === 'runSheet' ? t.text : t.muted }}>
                  Home delivery run sheet
                </span>
              </div>
              <div
                style={{ paddingLeft: 24, display: 'flex', gap: 8, flexWrap: 'wrap' }}
                onClick={(e) => e.stopPropagation()}
              >
                <DatePicker
                  format="DD/MM/YYYY"
                  value={runDay}
                  allowClear={false}
                  onChange={(d) => { setRunDay(d); setMode('runSheet'); }}
                  disabledDate={disabledDate}
                  style={{ flex: '1 1 150px' }}
                />
                <TimePicker.RangePicker
                  format="HH:mm"
                  minuteStep={15}
                  value={runTimes}
                  allowClear={false}
                  onChange={(v) => { setRunTimes(v); setMode('runSheet'); }}
                  style={{ flex: '1 1 170px' }}
                />
              </div>
              {mode === 'runSheet' && !window_.error && (
                <div style={{ paddingLeft: 24 }} onClick={(e) => e.stopPropagation()}>
                  <Select
                    mode="multiple"
                    allowClear
                    style={{ width: '100%' }}
                    loading={previewing}
                    value={areaIds}
                    onChange={setAreaIds}
                    placeholder={
                      previewing ? 'Reading the window…'
                        : `All areas${preview ? ` (${preview.meta.areas.length})` : ''}`
                    }
                    maxTagCount="responsive"
                    // The window's own thanas, in the order the sheet prints
                    // them. Never the whole upazila table: nobody finds Kafrul
                    // in a list of five hundred.
                    options={(preview?.meta.areas ?? []).map((a) => ({
                      value: a.id,
                      label: `${a.name} (${a.count})`,
                      disabled: a.id === null,
                    }))}
                    filterOption={(input, option) =>
                      (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                  />
                </div>
              )}
              <div style={{ paddingLeft: 24, font: '400 11.5px Outfit, sans-serif', color: t.dim }}>
                Orders that <em>became</em> READY in that window and are currently READY — DCC + GCC home delivery.
                {mode === 'runSheet' && !window_.error && !previewing && preview && (
                  <span>
                    {' '}<strong style={{ color: t.activeText }}>{selectedCount}</strong> order
                    {selectedCount === 1 ? '' : 's'} across{' '}
                    {areaIds.length || preview.meta.areas.length} area
                    {(areaIds.length || preview.meta.areas.length) === 1 ? '' : 's'}.
                  </span>
                )}
                {' '}Print gives one page per thana, so the stack splits by area.
              </div>
            </div>

            {/* Courier delivery — same idea, different channels, no thana split. */}
            <div onClick={() => setMode('courierSheet')} style={optionStyle(mode === 'courierSheet')}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                <span style={radio(mode === 'courierSheet')}>{mode === 'courierSheet' && '✓'}</span>
                <span style={{ font: '600 13px Outfit, sans-serif', color: mode === 'courierSheet' ? t.text : t.muted }}>
                  Courier delivery run sheet
                </span>
              </div>
              <div
                style={{ paddingLeft: 24, display: 'flex', gap: 8, flexWrap: 'wrap' }}
                onClick={(e) => e.stopPropagation()}
              >
                <DatePicker
                  format="DD/MM/YYYY"
                  value={courierDay}
                  allowClear={false}
                  onChange={(d) => { setCourierDay(d); setMode('courierSheet'); }}
                  disabledDate={disabledDate}
                  style={{ flex: '1 1 150px' }}
                />
                <TimePicker.RangePicker
                  format="HH:mm"
                  minuteStep={15}
                  value={courierTimes}
                  allowClear={false}
                  onChange={(v) => { setCourierTimes(v); setMode('courierSheet'); }}
                  style={{ flex: '1 1 170px' }}
                />
              </div>
              {mode === 'courierSheet' && !courierWindow.error && (
                <div style={{ paddingLeft: 24 }} onClick={(e) => e.stopPropagation()}>
                  <Select
                    mode="multiple"
                    allowClear
                    style={{ width: '100%' }}
                    loading={courierPreviewing}
                    value={channelIds}
                    onChange={setChannelIds}
                    placeholder={
                      courierPreviewing ? 'Reading the window…'
                        : `All channels${courierPreview ? ` (${courierPreview.meta.channels.length})` : ''}`
                    }
                    maxTagCount="responsive"
                    options={(courierPreview?.meta.channels ?? []).map((ch) => ({
                      value: ch.id,
                      label: `${ch.name} (${ch.count})`,
                      disabled: ch.id === null,
                    }))}
                    filterOption={(input, option) =>
                      (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                  />
                </div>
              )}
              <div style={{ paddingLeft: 24, font: '400 11.5px Outfit, sans-serif', color: t.dim }}>
                Orders that <em>became</em> READY in that window and are currently READY — courier channels.
                {mode === 'courierSheet' && !courierWindow.error && !courierPreviewing && courierPreview && (
                  <span>
                    {' '}<strong style={{ color: t.activeText }}>{courierSelectedCount}</strong> order
                    {courierSelectedCount === 1 ? '' : 's'} across{' '}
                    {channelIds.length || courierPreview.meta.channels.length} channel
                    {(channelIds.length || courierPreview.meta.channels.length) === 1 ? '' : 's'}.
                  </span>
                )}
              </div>
            </div>

          </div>
        </Modal>
      </ConfigProvider>
    </Fragment>
  );
};

export default ExportOrderExcel;
